import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const TOPMATE_URL = 'https://topmate.io/acharya_ji_ajay_l_agrawal/2021138?utm_source=public_profile&utm_campaign=acharya_ji_ajay_l_agrawal';

const deliverables = [
  'Personal Dasha-Antardasha analysis — where you are in your cosmic cycle right now',
  'Identification of your strongest planetary period for business growth',
  'Vastu assessment checklist for your primary workspace',
  'Specific Muhurta windows for your most pressing upcoming decision',
  'Practical remedies — no expensive gemstones, no complex rituals',
  'Session recording and strategic PDF blueprint',
];

const objections = [
  {
    q: 'Is the investment worth it?',
    a: 'A single Muhurta date that prevents a poor partnership or investment can save many multiples of that. The question is whether you can afford to proceed without knowing.',
  },
  {
    q: 'I am skeptical of astrology.',
    a: 'Acharyaji does not ask for belief. He asks for your birth data. The mathematics of Vedic Astrology has been refined over 5,000 years. Judge the output for yourself.',
  },
  {
    q: 'What if I miss the slot?',
    a: 'Slots are released weekly on Topmate. The waitlist grows steadily. Early movers receive priority scheduling.',
  },
];

export default function PricingCTA() {
  return (
    <section id="consult" className="py-28 px-6 md:px-12 bg-brand-light border-t border-stone-200">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-brand-gold text-sm uppercase tracking-[0.3em] font-bold mb-4">
            One Session. Total Clarity.
          </p>
          <h2 className="text-4xl md:text-6xl font-serif text-brand-dark mb-6">
            The Clarity Consultation
          </h2>
          <p className="text-stone-600 font-medium text-xl max-w-2xl mx-auto leading-relaxed">
            Focused Vedic Astrology and Vastu advisory. Conducted globally via video call. Booked instantly on Topmate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {/* Deliverables Card */}
          <div className="bg-white border border-stone-200 rounded-sm p-10 md:p-12 shadow-md">
            <h3 className="font-serif text-3xl text-brand-dark mb-8">What You Walk Away With</h3>
            <ul className="space-y-6">
              {deliverables.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <SafeIcon name="Check" className="text-brand-gold mt-1.5 flex-shrink-0 text-xl" />
                  <span className="text-stone-700 font-medium text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Booking Card */}
          <div className="bg-brand-dark rounded-sm p-10 md:p-12 flex flex-col justify-between shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/10 rounded-bl-full pointer-events-none"></div>
            <div className="relative z-10">
              <p className="text-brand-gold text-sm uppercase tracking-widest font-bold mb-6">Book Your Session</p>
              <p className="text-stone-200 text-xl font-medium leading-relaxed mb-10">
                Instant booking via Topmate. Sessions conducted globally over Zoom or Google Meet. Pre-session chart analysis included.
              </p>
              <div className="space-y-5 mb-10">
                {[
                  'Instant booking via Topmate',
                  'Zoom or Google Meet, any timezone',
                  'Pre-session chart analysis included',
                  'Session recording provided',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <SafeIcon name="Check" className="text-brand-gold text-xl flex-shrink-0" />
                    <span className="text-stone-200 text-lg font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5 relative z-10">
              <motion.a
                href={TOPMATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full block py-6 bg-brand-gold text-brand-dark font-bold text-xl text-center hover:bg-white transition-colors duration-400 rounded-sm tracking-wide shadow-lg"
              >
                Book Consultation
              </motion.a>
              <p className="text-sm text-stone-400 text-center font-medium">
                Secure payment · Instant confirmation · Reschedule if needed
              </p>
            </div>
          </div>

          {/* Spotlight Section: Still Considering */}
          <div className="bg-brand-dark text-brand-light rounded-sm p-10 md:p-16 shadow-2xl relative overflow-hidden lg:col-span-2">
            <div className="absolute inset-0 bg-subtle-texture opacity-20 pointer-events-none"></div>
            <div className="relative z-10 text-center mb-12">
              <h3 className="font-serif text-4xl md:text-5xl text-brand-gold mb-4">Still Considering?</h3>
              <p className="text-stone-300 text-lg md:text-xl font-medium max-w-2xl mx-auto">
                It is natural to have questions before committing to an advisory session. Here is what most people ask.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10 mb-16">
              {objections.map((obj, i) => (
                <div key={i} className="bg-white/5 border border-brand-gold/20 backdrop-blur-sm rounded-sm p-8 hover:bg-white/10 transition-colors">
                  <p className="font-serif text-brand-gold text-xl mb-4 italic">"{obj.q}"</p>
                  <p className="text-stone-300 font-medium text-base leading-relaxed">{obj.a}</p>
                </div>
              ))}
            </div>
            <div className="text-center relative z-10 pt-8 border-t border-brand-gold/20">
              <p className="font-serif text-3xl text-white mb-2 italic">Clarity reduces pressure.</p>
              <p className="font-serif text-3xl text-brand-gold mb-10 italic">Stability improves decisions.</p>
              <a
                href={TOPMATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-14 py-6 bg-brand-gold text-brand-dark font-bold text-xl hover:bg-white transition-all duration-400 rounded-sm tracking-wide shadow-xl"
              >
                Book Your Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}